import FontIcon from 'components/icons/FontIcon';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { HelpBox } from 'components/v2/help-boxes';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import React, { useEffect, useState } from 'react';
import {
  useCreateProductionSiteAddress,
  useDeleteProductionSiteAddress,
  useUpdateProductionSiteAddress,
} from 'services/api/mutations';
import { useCountriesLookup, useRegionsLookup } from 'services/api/queries';
import {
  DoubleFieldGrid,
  ExpandablePanelHeaderName,
  ExpandablePanelModule,
  HeaderCaptionSemiBold,
  HorizontalDivider,
} from 'styles/v2/Styles.styled';
import { EPDProductInformationModel, Option, ProductionSiteModel } from 'types/types';
import { DeleteProductIconButton } from '../../ProductInformationTab';
import ToggleButton from '../../panels/toggleable/ToggleButton';
import usePcrV2 from '../../hooks/usePcrV2';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import { TooltipPosition } from 'components/controls/Tooltip';
import getAvailableRegionsForCountryOptions from 'components/company/getAvailableRegionsForCountryOptions';
import useIsCreatedByApiFullyIntegratedPreverifiedTool from '../../hooks/useIsCreatedByApiFullyIntegratedPreverifiedTool';

const ProductionSitesSection: React.FunctionComponent<{
  product?: EPDProductInformationModel;
  epdVersionId: string;
  errors: any;
}> = ({ product, epdVersionId, errors }) => {
  const [productionSitesRows, setProductionSitesRows] = useState<ProductionSiteModel[] | undefined>(
    product?.productionSites
  );
  const countries = useCountriesLookup().data;
  const createMutation = useCreateProductionSiteAddress(epdVersionId);
  const deleteMutation = useDeleteProductionSiteAddress(epdVersionId);
  const updateMutation = useUpdateProductionSiteAddress(epdVersionId);
  const isPcrV2 = usePcrV2();
  const isReadOnly = useIsReadOnlyMode();
  const isCreatedByApi = useIsCreatedByApiFullyIntegratedPreverifiedTool();
  const regions = useRegionsLookup().data;

  const productionSiteAddressesErrors = (errors: any, index: any) => {
    return errors?.productionSiteBlock?.[`productionSiteAddresses[${index}]`];
  };

  useEffect(() => {
    setProductionSitesRows(product?.productionSites);
  }, [product?.productionSites]);

  const handleProductionSiteAddressUpdate = (field: string, value: string, id: string) => {
    const updateAddress = product?.productionSites?.find((item) => item.id === id);
    const newData = { ...updateAddress, [field]: value } as ProductionSiteModel;

    if (field === 'countryId') {
      newData.regionId = '';
    }

    updateMutation.mutate(newData);
  };

  const handleAddRow = (product: EPDProductInformationModel) => {
    createMutation.mutate({ productId: product.id, epdVersionId });
    const newRowValues = [
      ...(productionSitesRows ?? []),
      {
        id: '',
        epdVersionId,
        nameOfManufacturer: '',
        name: '',
        city: '',
        countryId: '',
        zipCode: '',
        addressLine: '',
        addressLineSecond: '',
        regionId: '',
      },
    ];
    setProductionSitesRows(newRowValues);
  };

  const confirmDeleteProductionSiteAddress = (address: any) => {
    confirmDelete({
      caption: isPcrV2
        ? 'Are you sure you want to delete manufacturing site address?'
        : 'Are you sure you want to delete production site address?',
      onConfirm: () => deleteMutation.mutate(address.id as string),
    });
  };

  const selectedCountry = (item: ProductionSiteModel) => {
    if (item?.countryId) {
      return {
        value: item.countryId,
        label: countries?.find((country: any) => country.id === item.countryId)?.name,
      } as Option;
    }
    return null;
  };

  function noErrorsAtProductionSitesBlock(errors: any): boolean | undefined {
    const fieldNames: string[] = ['productionSiteBlock'];

    return !errors || !fieldNames.some((field) => field in errors);
  }

  const availableRegionsForCountry = (item: ProductionSiteModel) => {
    return getAvailableRegionsForCountryOptions(item?.countryId || '', regions || []);
  };

  const selectedRegion = (item: ProductionSiteModel) => {
    const regionsForCountry = availableRegionsForCountry(item);
    const region = regionsForCountry.find((region) => region.value === item.regionId);
    return region ? region : null;
  };

  const headerModuleTemplate = (
    options: any,
    header: string,
    tooltip?: string,
    tooltipPosition?: TooltipPosition,
    required?: boolean,
    isFilledIn?: boolean
  ) => {
    return (
      <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
        <ExpandablePanelHeaderName>
          <HeaderCaptionSemiBold>{header}</HeaderCaptionSemiBold>
          {tooltip && <TooltipHelpIcon content={tooltip} position={tooltipPosition} />}
          {required && !isFilledIn && <TooltipErrorIcon />}
          <ToggleButton options={options} />
        </ExpandablePanelHeaderName>
      </div>
    );
  };

  return (
    <ExpandablePanelModule
      headerTemplate={(options) =>
        headerModuleTemplate(
          options,
          isPcrV2 ? 'Manufacturing site(s)' : 'Production site(s)',
          isPcrV2
            ? 'Location of the manufacturing site(s), including, as a minimum, the city (or municipality, if not located in a city).'
            : 'Location of the production site(s), including, as a minimum, the city (or municipality, if not located in a city).',
          'right',
          true,
          noErrorsAtProductionSitesBlock(errors)
        )
      }
      toggleable
    >
      {productionSitesRows?.map((item, index) => (
        <DoubleFieldGrid key={item.id}>
          {index > 0 && (
            <React.Fragment>
              <HorizontalDivider className="w-full" style={{ margin: 0 }} />
              <div className="w-full" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <HeaderCaptionSemiBold>
                  {isPcrV2 ? 'Manufacturing site' : 'Production site'} {index + 1}
                </HeaderCaptionSemiBold>
                {!isReadOnly && (
                  <DeleteProductIconButton
                    icon={<FontIcon style={{ fontSize: '0.75rem' }} icon="delete" />}
                    text
                    onClick={() => confirmDeleteProductionSiteAddress(item)}
                  />
                )}
              </div>
            </React.Fragment>
          )}
          {isPcrV2 && (
            <WizardTextInputComponent
              label={'Name of manufacturer'}
              placeholder="Type here"
              value={item.nameOfManufacturer}
              onChanged={(field: string, value: string) => handleProductionSiteAddressUpdate(field, value, item.id)}
              name="nameOfManufacturer"
              tooltip="If different from the EPD Owner"
            />
          )}
          <WizardTextInputComponent
            label={isPcrV2 ? 'Manufacturing site name' : 'Production site name'}
            required
            placeholder="Type here"
            value={item.name}
            onChanged={(field: string, value: string) => handleProductionSiteAddressUpdate(field, value, item.id)}
            name="name"
            error={productionSiteAddressesErrors(errors, index)?.name}
          />
          <CompilerSelectControlledComponent
            label="Country"
            name="country"
            options={countries?.map((country: any) => {
              return {
                value: country?.id,
                label: country?.name,
              } as Option;
            })}
            required
            value={selectedCountry(item)}
            onChanged={(field: string, value: string) => handleProductionSiteAddressUpdate('countryId', value, item.id)}
            placeholder="Select"
            error={productionSiteAddressesErrors(errors, index)?.country}
          />

          {availableRegionsForCountry(item).length > 0 && (
            <CompilerSelectControlledComponent
              label="Region"
              name="region"
              options={availableRegionsForCountry(item)}
              required={!isCreatedByApi}
              value={selectedRegion(item)}
              onChanged={(field: string, value: string) => handleProductionSiteAddressUpdate('regionId', value, item.id)}
              placeholder="Select"
              error={productionSiteAddressesErrors(errors, index)?.region}
            />
          )}

          <WizardTextInputComponent
            label="City / Municipality"
            required
            placeholder="Type here"
            value={item.city}
            onChanged={(field: string, value: string) => handleProductionSiteAddressUpdate(field, value, item.id)}
            name="city"
            error={productionSiteAddressesErrors(errors, index)?.city}
          />
          <WizardTextInputComponent
            label="ZIP code"
            tooltipPosition={'right'}
            required
            placeholder="Type here"
            value={item.zipCode}
            onChanged={(field: string, value: string) => handleProductionSiteAddressUpdate(field, value, item.id)}
            name="zipCode"
            error={productionSiteAddressesErrors(errors, index)?.zipCode}
          />
          <WizardTextInputComponent
            label="Address line 1"
            placeholder="Type here"
            value={item.addressLine}
            onChanged={(field: string, value: string) => handleProductionSiteAddressUpdate(field, value, item.id)}
            name="addressLine"
          />
          <WizardTextInputComponent
            label="Address line 2"
            placeholder="Type here"
            value={item.addressLineSecond}
            onChanged={(field: string, value: string) => handleProductionSiteAddressUpdate(field, value, item.id)}
            name="addressLineSecond"
          />
        </DoubleFieldGrid>
      ))}
      <div>
        <HelpBox>
          <span>
            You can add more addresses if the product is produced in more than one {isPcrV2 ? 'manufacturing' : 'production'}{' '}
            site
          </span>
        </HelpBox>
        <WizardAddButton
          label="+ Add Address"
          onClick={() => {
            handleAddRow(product as EPDProductInformationModel);
          }}
        />
      </div>
    </ExpandablePanelModule>
  );
};

export default ProductionSitesSection;
